/**
 * IndividualMetricInsightsView - Compact presentation for individual metric insights
 *
 * Renders metric selector dropdown and line chart.
 * Uses `h-full` on root VStack to fill the CSS Grid cell on desktop;
 * on mobile the parent has auto height so `h-full` is a no-op.
 * Root VStack uses `gap-2` for compact spacing to match Dot Plot height
 * in the bottom grid row. Chart and legend are wrapped in a `flex-1` VStack
 * so extra grid cell height is absorbed below the legend, keeping the chart
 * (fixed `h-80`) tight against the header above.
 * Legend dot colors are passed from the Container via `legendColors` to stay
 * in sync with the chart line colors resolved from tailwind.config.
 * The legend row wraps, and the two name entries are capped to one line
 * because player names are arbitrary user data. The position entry is
 * deliberately uncapped — it is generated from the player's own position
 * abbreviations and has to read in full (SE-6291).
 * Conditionally renders an orange (#f97316) legend entry when a comparison
 * player is active, matching the orange trend line in the chart data.
 * @remarks This is a View component - handles presentation only
 */

import { SkFont } from "@shopify/react-native-skia";
import React, { memo } from "react";

import { TEST_ID_PLAYER_DETAIL } from "e2e/testId";

import LegendDot from "@/features/player-detail/components/molecules/LegendDot";

import PlatformChart from "@/components/molecules/PlatformChart";
import { Box } from "@/components/atoms/Box";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";

import { METRIC_INSIGHTS_X_KEY } from "@/features/player-detail/utils/metricInsightsAxisTicks";
import { renderMetricShellDropdown } from "@/features/player-detail/utils/metricShellDropdown";
import {
  renderShellOption,
  type ShellDropdownWiring,
} from "@/features/player-detail/utils/performanceAnalyticsViewHelpers";

import type {
  IndividualMetricType,
  GkIndividualMetricType,
} from "@/features/player-detail/constants";

/**
 * Metric option for dropdown selector (supports both outfield and GK metrics)
 */
interface MetricSelectOption {
  readonly value: IndividualMetricType | GkIndividualMetricType;
  readonly label: string;
}

/**
 * Chart data point structure compatible with PlatformChart
 */
interface ChartDataPoint {
  readonly [key: string]: string | number;
}

/**
 * Theme classes for the component
 */
interface ThemeClasses {
  readonly containerClass: string;
  readonly headingClass: string;
  readonly textClass: string;
}

/**
 * Resolved chart colors passed from the Container so legend dots
 * match the actual chart line colors derived from tailwind.config.
 */
interface LegendColors {
  readonly player: string;
  readonly position: string;
  readonly teamAverage: string;
}

/**
 * Props for IndividualMetricInsightsView component
 */
interface IndividualMetricInsightsViewProps {
  /** Whether dark mode is active */
  readonly isDark: boolean;
  /** Currently selected metric (outfield or GK) */
  readonly selectedMetric: IndividualMetricType | GkIndividualMetricType;
  /** Available metric options for dropdown */
  readonly metricOptions: readonly MetricSelectOption[];
  /** Factory for creating stable metric select handlers */
  readonly createSelectHandler: (option: MetricSelectOption) => () => void;
  /** Shell open-state + positioning wiring from the Container (SE-5568) */
  readonly dropdown: ShellDropdownWiring;
  /** Shell-hosted trigger element from the Container */
  readonly trigger: React.JSX.Element;
  /** X-axis label formatter for chart */
  readonly formatXLabel: (value: string | number) => string;
  /** Chart data points */
  readonly chartData: readonly ChartDataPoint[];
  /** Y-axis keys for chart lines */
  readonly yKeys: readonly string[];
  /** Color mapping for chart lines */
  readonly colors: Record<string, string>;
  /** Human-readable labels for chart yKeys (for tooltips) */
  readonly yKeyLabels: Record<string, string>;
  /** Font for chart labels */
  readonly font: SkFont | null;
  /** Color for axis labels */
  readonly labelColor: string;
  /** Current player name */
  readonly playerName: string;
  /** Position label for legend */
  readonly positionLabel: string;
  /** X-axis tick count to show every season year */
  readonly xTickCount: number;
  /**
   * The season years to pin x-axis ticks to (SE-7139).
   *
   * Without these the chart libraries choose their own tick positions from the
   * count alone, and on native a season they skip renders as a point with no
   * year beneath it.
   */
  readonly xTickValues: readonly number[];
  /** Whether data is loading */
  readonly isLoading: boolean;
  /** Theme classes computed by container */
  readonly theme: ThemeClasses;
  /** Comparison player name for orange legend entry (null when no comparison active) */
  readonly comparisonPlayerName: string | null;
  /** Resolved chart colors for legend dots — keeps legend in sync with chart lines */
  readonly legendColors: LegendColors;
}

/**
 * IndividualMetricInsightsView renders the metric insights UI
 * @param props - View props
 * @param props.isDark - Whether dark mode is active
 * @param props.selectedMetric - Currently selected metric
 * @param props.metricOptions - Available metric options for dropdown
 * @param props.createSelectHandler - Factory for stable metric select handlers
 * @param props.dropdown - Shell open-state + positioning wiring (SE-5568)
 * @param props.trigger - Shell-hosted trigger element
 * @param props.formatXLabel - X-axis label formatter for chart
 * @param props.chartData - Chart data points
 * @param props.yKeys - Y-axis keys for chart lines
 * @param props.colors - Color mapping for chart lines
 * @param props.yKeyLabels - Human-readable labels for chart yKeys (for tooltips)
 * @param props.font - Font for chart labels
 * @param props.labelColor - Color for axis labels
 * @param props.playerName - Current player name
 * @param props.positionLabel - Position label for legend
 * @param props.xTickCount - Number of tick marks to display on the x-axis
 * @param props.xTickValues - Season years to pin the x-axis ticks to
 * @param props.isLoading - Whether data is loading
 * @param props.theme - Theme classes computed by container
 * @param props.comparisonPlayerName - Comparison player name for orange legend entry
 * @param props.legendColors - Resolved chart colors for legend dots
 * @returns Rendered component
 */
const IndividualMetricInsightsView = ({
  isDark,
  selectedMetric,
  metricOptions,
  createSelectHandler,
  dropdown,
  trigger,
  formatXLabel,
  chartData,
  yKeys,
  colors,
  yKeyLabels,
  font,
  labelColor,
  playerName,
  positionLabel,
  xTickCount,
  xTickValues,
  isLoading,
  theme,
  comparisonPlayerName,
  legendColors,
}: IndividualMetricInsightsViewProps) => (
  <Stack
    className={`relative size-full gap-2 rounded-md p-4 ${theme.containerClass}`}
  >
    {/* Header */}
    <Stack direction="horizontal" className="items-center justify-between">
      <Text
        variant="title"
        className={isDark ? "text-white" : "text-content-primary"}
      >
        Individual Metric Insights
      </Text>
      {renderMetricShellDropdown({
        wiring: dropdown,
        trigger,
        panelClassName: "max-w-64 overflow-hidden py-1",
        options: metricOptions.map(option =>
          renderShellOption({
            id: String(option.value),
            label: option.label,
            isSelected: option.value === selectedMetric,
            onPress: createSelectHandler(option),
            isDark,
          })
        ),
      })}
    </Stack>

    {/* Chart + Legend wrapper — flex-1 absorbs extra grid cell height below legend */}
    <Stack className="flex-1 gap-2">
      {isLoading ? (
        <Box className="h-80 w-full" />
      ) : chartData.length > 0 ? (
        <Box className="h-80 w-full">
          <PlatformChart
            data={chartData as { [key: string]: string | number }[]}
            xKey={METRIC_INSIGHTS_X_KEY}
            yKeys={yKeys as string[]}
            colors={colors}
            yKeyLabels={yKeyLabels}
            font={font}
            width={700}
            height={350}
            /*
             * SE-7139: the right edge gets 24, the other three keep 16.
             *
             * victory-native draws an x-axis label only if its whole box clears
             * the plot's right edge — `labelX + labelWidth < chartBounds.right`
             * (victory-native XAxis.tsx:71, gating the render at :140). For a
             * domain padding `p` and plot width `W` the rightmost tick sits
             * `p*W/(W+2p)` inside that edge. "2024" in Satoshi Regular at 12px
             * — the Skia font from `useSafeFont(12)`; the `fontSize: 14` below
             * is web-only — measures 29.21px, so it needs 14.60px of clearance.
             * At 16 the clearance is 13.8-14.6px for every plot width under
             * ~335px, and this chart is roughly `screenWidth - 98` wide, so on
             * a phone the label was lost by under a pixel. At 24 it clears by
             * 19.4-20.9px across that range.
             *
             * Only `right` moves. Raising all four sides would also pad the y
             * domain, which `.nice()` then re-buckets — measured at 47% of
             * realistic y-ranges landing on a different domain and 29% on a
             * different set of gridlines. That is a visual change this ticket
             * did not ask for and cannot verify headlessly.
             */
            domainPadding={{ left: 16, right: 24, top: 16, bottom: 16 }}
            padding={40}
            xAxis={{
              labelColor,
              fontSize: 14,
              formatXLabel,
              tickCount: xTickCount,
              tickValues: [...xTickValues],
            }}
            yAxis={[{ labelColor, fontSize: 14 }]}
          />
        </Box>
      ) : (
        <Box className="flex h-80 w-full items-center justify-center">
          <Text variant="unstyled" className={`text-body ${theme.textClass}`}>
            No data available
          </Text>
        </Box>
      )}

      {/* Legend — wraps, so a long entry takes a new line instead of overflowing */}
      <Stack
        direction="horizontal"
        testID={TEST_ID_PLAYER_DETAIL.METRIC_INSIGHTS_LEGEND}
        className="flex-wrap items-center justify-center gap-x-6 gap-y-2"
      >
        <Stack direction="horizontal" className="items-center gap-2">
          <LegendDot color={legendColors.player} />
          <Text
            variant="body"
            className={`max-w-28 ${isDark ? "text-white" : "text-content-secondary"}`}
            numberOfLines={1}
          >
            {playerName}
          </Text>
        </Stack>
        <Stack direction="horizontal" className="items-center gap-2">
          <LegendDot color={legendColors.position} />
          {/* No width cap here, unlike the name entries above and below:
              positionLabel is generated by formatPositionLabel from the
              player's own position abbreviations, so it is short and bounded
              and must read in full (SE-6291). */}
          <Text
            variant="body"
            testID={TEST_ID_PLAYER_DETAIL.METRIC_INSIGHTS_LEGEND_POSITION}
            className={`${isDark ? "text-white" : "text-content-secondary"}`}
          >
            {positionLabel}
          </Text>
        </Stack>
        <Stack direction="horizontal" className="items-center gap-2">
          <LegendDot color={legendColors.teamAverage} />
          <Text
            variant="body"
            className={`${isDark ? "text-white" : "text-content-secondary"}`}
          >
            Team Average
          </Text>
        </Stack>
        {comparisonPlayerName ? (
          <Stack direction="horizontal" className="items-center gap-2">
            <LegendDot color="#f97316" />
            <Text
              variant="body"
              className={`max-w-28 ${isDark ? "text-white" : "text-content-secondary"}`}
              numberOfLines={1}
            >
              {comparisonPlayerName}
            </Text>
          </Stack>
        ) : null}
      </Stack>
    </Stack>
  </Stack>
);

IndividualMetricInsightsView.displayName = "IndividualMetricInsightsView";

export default memo(IndividualMetricInsightsView);
